import { FC, useEffect, useMemo, useRef, useState } from "react";

import {
  Button,
  ButtonText,
  HStack,
  Pressable,
  Spinner,
  Text,
  VStack,
} from "@/components/ui";
import API from "@/hooks/api";
import type { GroupSize } from "@/types/utils";

import { Chip, FieldLabel, Segmented } from "./Controls";
import { STATES } from "./geo";
import Sheet from "./Sheet";
import {
  buildStudyRequest,
  estimateCredits,
  FEEDS_LABEL,
  officesInState,
  studyQuestions,
} from "./survey";
import type { Office, Race } from "./types";
import useFieldWave, {
  MAX_IN_FLIGHT,
  RUN_DELAY_MS,
  RUN_MINUTES,
  type TaskStatus,
  type WaveTask,
} from "./useFieldWave";
import type { TrackerStudy } from "./useMidtermsData";
import { OFFICE_LABEL } from "./view";

interface FieldWaveSheetProps {
  /** False until the existing tracker studies have been listed. */
  listed: boolean;
  onClose: () => void;
  /** Called once a wave finished launching, to reload the tracker. */
  onLaunched: () => void;
  open: boolean;
  races: Race[];
  studies: TrackerStudy[];
}

type Scope = "all" | "competitive" | "custom" | "races";

const SIZES: { label: string; value: GroupSize }[] = [
  { label: "300", value: 300 },
  { label: "500", value: 500 },
  { label: "1,000", value: 1000 },
  { label: "1,500", value: 1500 },
];

const SCOPES: { label: string; value: Scope }[] = [
  { label: "Competitive races", value: "competitive" },
  { label: "States with a race", value: "races" },
  { label: "All 50 states", value: "all" },
  { label: "Pick states", value: "custom" },
];

const STATUS_LABEL: Record<TaskStatus, string> = {
  created: "Study created, waiting to run",
  creating: "Creating study…",
  failed: "Failed",
  finished: "Run finished",
  queued: "Queued",
  started: "Running",
  starting: "Starting run…",
};

const ALL_OFFICES: Office[] = ["senate", "governor", "house"];
const VOTING_STATES = STATES.filter((s) => s.abbr !== "DC");

/** Creates the next wave's studies and launches their runs. */
const FieldWaveSheet: FC<FieldWaveSheetProps> = ({
  listed,
  onClose,
  onLaunched,
  open,
  races,
  studies,
}) => {
  const [feedStudies, setFeedStudies] = useState(false);
  const [includeContext, setIncludeContext] = useState(false);
  const [includeWhy, setIncludeWhy] = useState(false);
  const [offices, setOffices] = useState<Office[]>(ALL_OFFICES);
  const [picked, setPicked] = useState<string[]>([]);
  const [scope, setScope] = useState<Scope>("competitive");
  const [size, setSize] = useState<GroupSize>(1000);

  const {
    balance,
    error: balanceError,
    loading: balanceLoading,
    refetch: refetchBalance,
  } = API.Billing.useGetBalance();
  const { reset, running, start, stop, tasks } = useFieldWave();

  // The next wave number comes from the existing tracker studies, so nothing
  // launches until they are listed (it would reuse an existing wave's name).
  const wave = studies.reduce((max, s) => Math.max(max, s.wave), 0) + 1;
  const pending = studies.filter((s) => s.lastRun === null);

  const plan = useMemo(() => {
    const selectedRaces = races.filter((r) => offices.includes(r.office));
    // Every state elects House members, so a House wave can go anywhere.
    const raceStates = new Set(
      offices.includes("house")
        ? VOTING_STATES.map((s) => s.abbr)
        : selectedRaces.map((r) => r.state)
    );
    // Toss-up or lean in Cook's latest ratings. The House has no statewide
    // rating, so a House-only wave uses the Senate and governor battlegrounds.
    const competitive = new Set(
      (selectedRaces.length > 0 ? selectedRaces : races)
        .filter((r) => r.cook && /toss|lean/i.test(r.cook))
        .map((r) => r.state)
    );
    const candidates =
      scope === "all"
        ? VOTING_STATES
        : scope === "races"
          ? VOTING_STATES.filter((s) => raceStates.has(s.abbr))
          : scope === "competitive"
            ? VOTING_STATES.filter((s) => competitive.has(s.abbr))
            : VOTING_STATES.filter((s) => picked.includes(s.abbr));
    return candidates.flatMap((state) => {
      const stateOffices = officesInState(state.abbr, races, offices);
      if (stateOffices.length === 0) return [];
      return (feedStudies ? [false, true] : [false]).map((feeds) => {
        const questions = studyQuestions(state, races, {
          feeds,
          includeContext,
          includeWhy,
          offices: stateOffices,
        });
        return {
          credits: estimateCredits(questions, size),
          feeds,
          offices: stateOffices,
          questions: questions.length,
          state,
        };
      });
    });
  }, [
    feedStudies,
    includeContext,
    includeWhy,
    offices,
    picked,
    races,
    scope,
    size,
  ]);

  const totalCredits = plan.reduce((sum, p) => sum + p.credits, 0);
  // Runs start 2 per minute, at most MAX_IN_FLIGHT at a time.
  const minutes = Math.max(
    Math.ceil((plan.length * RUN_DELAY_MS) / 60000),
    Math.ceil(plan.length / MAX_IN_FLIGHT) * RUN_MINUTES
  );
  const duration =
    minutes >= 90 ? `${Math.round(minutes / 30) / 2} hours` : `${minutes} min`;
  const runningCount = tasks.filter(
    (t) => t.status === "started" || t.status === "starting"
  ).length;
  const waitingCount = tasks.filter(
    (t) =>
      t.status === "created" || t.status === "creating" || t.status === "queued"
  ).length;
  // Unlimited wallets report a balance of -1 and are never short.
  const unlimited = balance === -1;
  // Launching waits for a known balance: a wave that runs out of credits
  // midway leaves studies created but never run.
  const affordable = balance !== null && (unlimited || balance >= totalCredits);
  const balanceLabel =
    balance === null
      ? null
      : unlimited
        ? "unlimited"
        : `${balance.toLocaleString("en-US")} credits`;

  // Reload the tracker (and the balance) once every run of a wave has been
  // launched.
  const wasRunning = useRef(false);
  useEffect(() => {
    if (wasRunning.current && !running && tasks.length > 0) {
      onLaunched();
      refetchBalance();
    }
    wasRunning.current = running;
  }, [onLaunched, refetchBalance, running, tasks.length]);

  const launch = () => {
    const date = new Date();
    void start(
      plan.map((p): WaveTask => ({
        error: null,
        feeds: p.feeds,
        request: buildStudyRequest(p.state, races, {
          date,
          feeds: p.feeds,
          includeContext,
          includeWhy,
          offices: p.offices,
          size,
          wave,
        }),
        runId: null,
        state: p.state.abbr,
        status: "queued",
        studyId: null,
        wave,
      }))
    );
  };

  const launchPending = () =>
    void start(
      pending.map((s): WaveTask => ({
        error: null,
        feeds: s.feeds,
        request: null,
        runId: null,
        state: s.state,
        status: "created",
        studyId: s.studyId,
        wave: s.wave,
      }))
    );

  const toggle = <T,>(list: T[], item: T) =>
    list.includes(item) ? list.filter((x) => x !== item) : [...list, item];

  const footer = running ? (
    <HStack className="items-center justify-between gap-3">
      <HStack className="items-center gap-2">
        <Spinner color="rgb(var(--color-primary-500))" size="small" />
        <Text className="text-sm text-typography-700">
          {waitingCount > 0
            ? `${runningCount} running · ${waitingCount} waiting · keep this tab open`
            : `${runningCount} running · all started; closing this tab won't stop them`}
        </Text>
      </HStack>
      <Button onPress={stop} size="sm" variant="outline">
        <ButtonText>Stop</ButtonText>
      </Button>
    </HStack>
  ) : tasks.length > 0 ? (
    <HStack className="items-center justify-end gap-3">
      <Button onPress={reset} size="sm" variant="outline">
        <ButtonText>Set up another wave</ButtonText>
      </Button>
      <Button className="rounded-xl" onPress={onClose} size="sm">
        <ButtonText>Done</ButtonText>
      </Button>
    </HStack>
  ) : (
    <HStack className="items-center justify-between gap-3">
      <Text className="flex-1 text-xs text-typography-500">
        {plan.length} studies · {totalCredits.toLocaleString("en-US")} credits
        {balanceLabel ? ` · balance ${balanceLabel}` : ""}
      </Text>
      <Button
        className="rounded-xl"
        isDisabled={!listed || plan.length === 0 || !affordable}
        onPress={() => {
          reset();
          launch();
        }}
        size="sm"
      >
        <ButtonText>
          Create & run {plan.length} survey{plan.length === 1 ? "" : "s"}
        </ButtonText>
      </Button>
    </HStack>
  );

  return (
    <Sheet
      footer={footer}
      onClose={onClose}
      open={open}
      subtitle={
        listed
          ? `Wave ${wave} · one study per state, named "Midterms 2026 · XX · Wave ${wave}"${feedStudies ? `, plus its "… · ${FEEDS_LABEL}" study` : ""}, in the folder Midterms 2026 › Wave ${wave}`
          : "Checking which waves already exist…"
      }
      title="Field a wave"
    >
      {tasks.length > 0 ? (
        <VStack className="gap-2">
          <FieldLabel label="Progress" />
          {tasks.map((task) => (
            <HStack
              className="items-center gap-3"
              key={`${task.state}-${task.wave}-${task.feeds ? "feeds" : "tracker"}`}
            >
              <Text className="w-10 text-sm font-semibold text-typography-900">
                {task.state}
              </Text>
              <Text
                className={`flex-1 text-sm ${task.status === "failed" ? "text-error-600" : "text-typography-700"}`}
                numberOfLines={2}
              >
                {task.feeds ? `${FEEDS_LABEL} · ` : ""}
                {STATUS_LABEL[task.status]}
                {task.error ? ` · ${task.error}` : ""}
              </Text>
            </HStack>
          ))}
          {!running && (
            <Text className="text-xs text-typography-500">
              Runs execute on Foresight’s servers; results show up here as each
              one completes (refresh the tracker).
            </Text>
          )}
        </VStack>
      ) : (
        <>
          <VStack className="gap-1.5">
            <FieldLabel label="Races to ask about" />
            <HStack className="flex-wrap gap-2">
              {ALL_OFFICES.map((office) => (
                <Chip
                  active={offices.includes(office)}
                  key={office}
                  label={
                    office === "house"
                      ? "House (generic ballot)"
                      : OFFICE_LABEL[office]
                  }
                  onPress={() => setOffices((list) => toggle(list, office))}
                />
              ))}
            </HStack>
          </VStack>

          <VStack className="gap-1.5">
            <FieldLabel label="States" />
            <Segmented onChange={setScope} options={SCOPES} value={scope} />
            {scope === "custom" && (
              <HStack className="flex-wrap gap-1.5 pt-2">
                {VOTING_STATES.map((state) => (
                  <Chip
                    active={picked.includes(state.abbr)}
                    key={state.abbr}
                    label={state.abbr}
                    onPress={() =>
                      setPicked((list) => toggle(list, state.abbr))
                    }
                  />
                ))}
              </HStack>
            )}
          </VStack>

          <VStack className="gap-1.5">
            <FieldLabel label="Respondents per state" />
            <Segmented onChange={setSize} options={SIZES} value={size} />
            <Text className="text-xs text-typography-400">
              1,000 gives a ±3 pt sampling error on a statewide share and enough
              respondents for the larger counties. Reporters read 1,000+ as
              authoritative.
            </Text>
          </VStack>

          <VStack className="gap-1.5">
            <FieldLabel label="Context questions" />
            <HStack className="gap-2">
              <Chip
                active={includeContext}
                label="Ask"
                onPress={() => setIncludeContext(true)}
              />
              <Chip
                active={!includeContext}
                label="Skip"
                onPress={() => setIncludeContext(false)}
              />
            </HStack>
            <Text className="text-xs text-typography-400">
              Who will win, Trump approval, top issue and household finances.
              Shown in the race panel but none of them moves the forecast.
            </Text>
          </VStack>

          <VStack className="gap-1.5">
            <FieldLabel label="Open-ended reason" />
            <HStack className="gap-2">
              <Chip
                active={includeWhy}
                label="Ask why (2 credits each)"
                onPress={() => setIncludeWhy(true)}
              />
              <Chip
                active={!includeWhy}
                label="Skip"
                onPress={() => setIncludeWhy(false)}
              />
            </HStack>
          </VStack>

          <VStack className="gap-1.5">
            <FieldLabel label="Partisan-feed study" />
            <HStack className="gap-2">
              <Chip
                active={feedStudies}
                label="Add one per state"
                onPress={() => setFeedStudies(true)}
              />
              <Chip
                active={!feedStudies}
                label="Skip"
                onPress={() => setFeedStudies(false)}
              />
            </HStack>
            <Text className="text-xs text-typography-400">
              A second study per state with the same briefing. Respondents give
              their 2024 vote first, then hear recent stories from the outlets
              their side follows: right-leaning for Trump voters, left-leaning
              for Harris voters, AP, Reuters and state papers for everyone else.
              Compared with the tracker study; not shown on the map. Doubles the
              runs.
            </Text>
          </VStack>

          <VStack className="gap-1.5">
            <FieldLabel label="Cost" />
            <Text className="text-sm text-typography-800">
              {plan.length} studies ·{" "}
              {(plan.length * size).toLocaleString("en-US")} respondents ·{" "}
              {totalCredits.toLocaleString("en-US")} credits
            </Text>
            <Text className="text-xs text-typography-500">
              {balanceLoading
                ? "Checking balance…"
                : balanceError
                  ? `Balance unavailable: ${balanceError}`
                  : balanceLabel
                    ? `Balance: ${balanceLabel}${affordable ? "" : " — not enough for this wave"}`
                    : ""}
            </Text>
            {balanceError && (
              <Pressable className="self-start" onPress={refetchBalance}>
                <Text className="text-xs font-medium text-primary-600">
                  Check the balance again
                </Text>
              </Pressable>
            )}
            <Text className="text-xs text-typography-500">
              Runs start 2 per minute and at most {MAX_IN_FLIGHT} at a time
              (about {RUN_MINUTES} min each), so this wave takes about{" "}
              {duration}. Keep this tab open until the last run starts.
            </Text>
          </VStack>

          {pending.length > 0 && (
            <VStack className="gap-1.5">
              <FieldLabel label="Never run" />
              <Text className="text-sm text-typography-700">
                {pending.length} stud
                {pending.length === 1 ? "y was" : "ies were"} created but never
                run (
                {pending
                  .map((s) => (s.feeds ? `${s.state} partisan news` : s.state))
                  .join(", ")}
                ).
              </Text>
              <Button
                className="self-start rounded-xl"
                isDisabled={balance === null || (!unlimited && balance <= 0)}
                onPress={() => {
                  reset();
                  launchPending();
                }}
                size="sm"
                variant="outline"
              >
                <ButtonText>Run them now</ButtonText>
              </Button>
            </VStack>
          )}
        </>
      )}
    </Sheet>
  );
};

export default FieldWaveSheet;
